/**
 * YouTube 外觀模式（lite-youtube facade）。
 *
 * **這支島嶼存在的理由**（[計劃 96](../../../documents/plan/96-舊站問題與新站調整確認表.md) C-2）：
 * 舊站對影片廣告直接輸出 `<iframe src="youtube.com/embed/...">`，
 * 頁面一載入就拉 YouTube 播放器 —— 約 1.5 MB 的第三方 JS，
 * 是規格 8.7 點名的 LCP／TBT 殺手。
 *
 * 做法：伺服器先渲染縮圖 + 播放鈕，**使用者實際點擊後才注入 iframe**。
 * 這同時滿足業主 2026-08-19 的要求（「要點擊撥放」）與計劃 16 的效能預算。
 *
 * ⚠️ 本檔案刻意維持極小且無相依 —— 它取代的是 1.5 MB，
 * 若自己長成 50 KB 就失去意義了。
 */

/** 伺服器渲染時放在容器上的屬性名。 */
const FACADE_SELECTOR = '[data-yt-facade]';
const VIDEO_ID_ATTR = 'data-yt-video-id';
const TITLE_ATTR = 'data-yt-title';

/**
 * 注入實際的播放器。
 *
 * `autoplay=1` 是安全的：這裡**必定**是使用者手勢觸發的，
 * 不會被瀏覽器的自動播放政策擋下，也不構成擾民自動播放。
 */
function activate(container: HTMLElement): void {
  const videoId = container.getAttribute(VIDEO_ID_ATTR);

  if (!videoId) {
    return;
  }

  const iframe = document.createElement('iframe');

  // 與 C# 端的 YoutubeUrl.BuildEmbedUrl 保持一致：
  // nocookie 網域 + rel=0，不帶 Flash 時代殘留的 wmode 參數。
  iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?rel=0&autoplay=1`;

  iframe.title = container.getAttribute(TITLE_ATTR) ?? '廣告影片';
  iframe.loading = 'lazy';
  iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
  iframe.allowFullscreen = true;
  iframe.className = 'absolute inset-0 h-full w-full border-0';

  // 直接換掉整個容器內容：外觀已完成任務，留著只會疊在 iframe 下面
  container.replaceChildren(iframe);
  container.removeAttribute('data-yt-facade');

  iframe.focus();
}

/**
 * 事件委派 —— 不逐一綁定。
 * 廣告是動態插入的（未來可能由島嶼重新渲染），委派可以不必管理生命週期。
 */
function onActivate(event: Event): void {
  const target = event.target as Element | null;
  const container = target?.closest<HTMLElement>(FACADE_SELECTOR);

  if (!container) {
    return;
  }

  event.preventDefault();
  activate(container);
}

document.addEventListener('click', onActivate);

// 鍵盤可及性（規格 10.3、WCAG 2.2 AA）：
// 外觀容器是 role="button"，因此必須同時支援 Enter 與 Space。
document.addEventListener('keydown', (event: KeyboardEvent) => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }

  const container = (event.target as Element | null)?.closest<HTMLElement>(FACADE_SELECTOR);

  if (container) {
    event.preventDefault();
    activate(container);
  }
});
